import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/ui/core";

export interface ProductStockBadgeProps {
  /** `undefined` = el catálogo no lleva la cuenta de este producto. */
  stock: number | undefined;
}

/** Por debajo de esto, quedan pocas y decirlo es información, no presión. */
export const LOW_STOCK_THRESHOLD = 5;

/**
 * Las existencias, como estado.
 *
 * ── Sin dato no se pinta nada ────────────────────────────────────────
 * `stock` es opcional en el contrato: un producto sin la cuenta llevada
 * NO está agotado, simplemente no se sabe. Pintar "0 disponibles" ahí
 * espantaría a quien iba a comprar algo que sí hay.
 *
 * ── Por qué es un Badge y no un rótulo ───────────────────────────────
 * Un badge comunica un ESTADO, que es justo lo que esto es — a diferencia
 * del rótulo de categoría de al lado, que solo clasifica y por eso va sin
 * cápsula.
 *
 * ── Las variantes elegidas son las que cumplen el contraste ──────────
 * `warning` (#9F6055 sobre #FBEAE4) e `info` (#5C4FD6 sobre #DCD4F0) se
 * quedan en 4.24:1 y 4.15:1, por debajo del 4.5:1 que exige un texto de
 * 11 px. Las que sí pasan son `error` (7.8:1) y `success` (4.8:1), y son
 * las dos que se usan. La deuda del Badge duplicado del design system
 * sigue abierta; esto no la resuelve, solo la esquiva por el lado seguro.
 */
export function ProductStockBadge({ stock }: ProductStockBadgeProps) {
  const { t } = useTranslation();

  if (stock === undefined) return null;

  const low = stock <= LOW_STOCK_THRESHOLD;
  const label =
    stock === 0
      ? t("Agotado")
      : low
        ? t("Solo quedan {{n}}", { n: stock })
        : t("En existencia");

  /*
   * El `shrink-0` va en un envoltorio y no en el Badge porque `core/Badge`
   * no acepta `className`. Sin él, el badge es un hijo de flex que se
   * encoge hasta partir "Solo quedan 3" en tres líneas cuando el nombre
   * del producto es largo.
   */
  return (
    <span className="shrink-0">
      <Badge variant={low ? "error" : "success"} size="sm">
        {label}
      </Badge>
    </span>
  );
}
